import React, { useState, useEffect, useCallback, useRef } from 'react';
import { apiFetch } from '../api/client';

// ─── Design tokens ────────────────────────────────────────────────────────────
const GREEN  = '#00e676';
const RED    = '#ff1744';
const AMBER  = '#ffab00';

const scoreColor = s =>
  s == null ? 'var(--bs-muted, #555)' : s >= 85 ? GREEN : s >= 70 ? AMBER : RED;

const sideColor = side =>
  (side || '').toUpperCase() === 'YES' ? GREEN : RED;

const fmt = n =>
  n == null ? '—' : Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// ─── Local primitives ─────────────────────────────────────────────────────────
const Card = ({ children, style }) => (
  <div style={{
    background: 'var(--bs-surface, #111)', border: '1px solid var(--bs-border, #1e1e1e)',
    borderRadius: 12, padding: 16, ...style
  }}>
    {children}
  </div>
);

const Label = ({ children, style }) => (
  <span style={{
    fontSize: 10, fontFamily: 'JetBrains Mono', color: 'var(--bs-muted, #555)',
    textTransform: 'uppercase', letterSpacing: 1, ...style
  }}>
    {children}
  </span>
);

const Skeleton = ({ w = '100%', h = 16, style }) => (
  <div style={{
    width: w, height: h, background: 'var(--bs-border, #1a1a1a)', borderRadius: 6,
    animation: 'pulse 1.5s ease-in-out infinite', flexShrink: 0, ...style
  }} />
);

// ─── Sparkline ────────────────────────────────────────────────────────────────
const UP = [[0,18],[15,14],[25,16],[35,10],[45,12],[55,6],[65,8],[75,3],[85,5],[100,0]];
const DN = [[0,0],[15,4],[25,2],[35,8],[45,5],[55,12],[65,9],[75,15],[85,13],[100,20]];

function Sparkline({ side }) {
  const pts = (side || '').toUpperCase() === 'YES' ? UP : DN;
  const color = sideColor(side);
  const poly = pts.map(([x, y]) => `${x},${y}`).join(' ');

  return (
    <svg viewBox="0 0 100 20" width={44} height={14} preserveAspectRatio="none" style={{ display: 'block' }}>
      <polyline points={poly} fill="none" stroke={color} strokeWidth={1} strokeDasharray="1 3" opacity={0.3} />
      {pts.map(([x, y], i) => {
        const op = 0.4 + (i / (pts.length - 1)) * 0.6;
        const last = i === pts.length - 1;
        return last
          ? <circle key={i} cx={x} cy={y} r={2.5} fill={color} opacity={op} />
          : <circle key={i} cx={x} cy={y} r={1.5} fill="none" stroke={color} strokeWidth={1.2} opacity={op} />;
      })}
    </svg>
  );
}

// ─── Position chip (for the horizontal strip) ────────────────────────────────
function PositionChip({ pos }) {
  const sc = sideColor(pos.side);
  return (
    <div style={{
      flexShrink: 0, background: 'var(--bs-surface, #111)',
      border: `1px solid ${sc}55`,
      borderRadius: 20, padding: '7px 14px',
      display: 'flex', alignItems: 'center', gap: 7,
    }}>
      <div style={{
        width: 6, height: 6, borderRadius: '50%', background: sc,
        animation: 'pulse 2s ease-in-out infinite', flexShrink: 0,
      }} />
      <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--bs-ink, #ddd)', whiteSpace: 'nowrap' }}>
        {pos.source}
      </span>
      <span style={{
        fontSize: 9, fontFamily: 'JetBrains Mono', fontWeight: 700,
        color: sc, background: `${sc}18`, borderRadius: 3, padding: '1px 6px',
      }}>
        {(pos.side || '').toUpperCase()}
      </span>
      <span style={{ fontSize: 10, fontFamily: 'JetBrains Mono', color: AMBER, fontWeight: 700 }}>
        ${fmt(pos.amount)}
      </span>
    </div>
  );
}

// ─── Latest label (above card) ───────────────────────────────────────────────
function LatestLabel({ source, publishedAt }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 5 }}>
      <div style={{ width: 6, height: 6, borderRadius: '50%', background: GREEN, flexShrink: 0, animation: 'pulse 2s ease-in-out infinite' }} />
      <span style={{ fontSize: 9, fontFamily: 'JetBrains Mono', fontWeight: 700, color: GREEN, letterSpacing: 1 }}>
        LATEST · {source}
      </span>
      <span style={{ fontSize: 9, fontFamily: 'JetBrains Mono', color: 'var(--bs-muted, #333)', marginLeft: 4 }}>
        {publishedAt}
      </span>
    </div>
  );
}

// ─── Article card ─────────────────────────────────────────────────────────────
function ArticleCard({ article, index, onTap }) {
  const { headline, paydirt_score, published_at, isLatest, bet } = article;
  const sc = scoreColor(paydirt_score);
  const side = bet?.side || 'YES';

  return (
    <div
      onClick={() => onTap(article)}
      style={{
        background: 'var(--bs-surface, #111)',
        border: isLatest ? '1px solid var(--bs-green-bd, #00e67633)' : '1px solid var(--bs-border, #1a1a1a)',
        borderRadius: 12, padding: '12px 14px', marginBottom: 10,
        cursor: 'pointer',
        animation: 'fadeUp 0.3s ease both',
        animationDelay: `${index * 40}ms`,
      }}
    >
      {/* Top: headline + score */}
      <div style={{ display: 'flex', gap: 12, marginBottom: bet ? 0 : 0 }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--bs-ink, #ddd)', lineHeight: 1.45, marginBottom: 6 }}>
            {headline}
          </div>
          {!isLatest && (
            <div style={{ fontSize: 9, fontFamily: 'JetBrains Mono', color: 'var(--bs-muted, #333)' }}>
              {published_at}
            </div>
          )}
        </div>
        <div style={{ flexShrink: 0, textAlign: 'right' }}>
          <div style={{ fontSize: 15, fontFamily: 'JetBrains Mono', fontWeight: 700, color: sc }}>
            {paydirt_score != null ? Math.round(paydirt_score) : '—'}
          </div>
          <div style={{ fontSize: 7, fontFamily: 'JetBrains Mono', color: 'var(--bs-muted, #444)', marginTop: 1 }}>SCORE</div>
        </div>
      </div>

      {/* YOUR BET box */}
      {bet && (
        <div style={{
          background: 'var(--bs-bg, #0d0d0d)', border: '1px solid var(--bs-border, #1a1a1a)',
          borderRadius: 8, padding: '8px 10px', marginTop: 8, marginBottom: 10,
        }}>
          <div style={{ fontSize: 9, fontFamily: 'JetBrains Mono', color: 'var(--bs-muted, #444)', letterSpacing: 1, marginBottom: 4 }}>
            YOUR BET
          </div>
          <div style={{ fontSize: 11, color: 'var(--bs-body, #888)', lineHeight: 1.4 }}>
            {bet.market}
          </div>
        </div>
      )}

      {/* Bet bar */}
      {bet && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{
              fontSize: 9, fontFamily: 'JetBrains Mono', fontWeight: 700,
              color: sideColor(side),
              background: `${sideColor(side)}1a`,
              border: `1px solid ${sideColor(side)}45`,
              padding: '2px 8px', borderRadius: 4,
            }}>
              {side}
            </span>
            <span style={{ fontSize: 10, fontFamily: 'JetBrains Mono', color: AMBER, fontWeight: 700 }}>
              ${fmt(bet.amount)}
            </span>
            <span style={{ fontSize: 9, fontFamily: 'JetBrains Mono', color: 'var(--bs-muted, #444)' }}>wagered</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Sparkline side={side} />
            <span style={{ fontSize: 9, fontFamily: 'JetBrains Mono', color: 'var(--bs-muted, #333)' }}>details ↑</span>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Skeleton feed row ────────────────────────────────────────────────────────
function SkeletonCard() {
  return (
    <div style={{ background: 'var(--bs-surface, #111)', border: '1px solid var(--bs-border, #1e1e1e)', borderRadius: 12, padding: '12px 14px', marginBottom: 10 }}>
      <Skeleton w="80%" h={14} style={{ marginBottom: 8 }} />
      <Skeleton w="40%" h={10} style={{ marginBottom: 10 }} />
      <Skeleton w="100%" h={40} style={{ marginBottom: 10 }} />
      <Skeleton w="60%" h={10} />
    </div>
  );
}

// ─── Bottom sheet ─────────────────────────────────────────────────────────────
function BottomSheet({ article, onClose, setPage }) {
  if (!article) return null;
  const { headline, source, paydirt_score, published_at, isLatest, bet, url } = article;
  const sc = scoreColor(paydirt_score);
  const side = bet?.side || 'YES';
  const sdColor = sideColor(side);

  return (
    <>
      {/* Overlay */}
      <div
        onClick={onClose}
        style={{ position: 'fixed', inset: 0, background: '#000000bb', zIndex: 50 }}
      />
      {/* Sheet */}
      <div style={{
        position: 'fixed', left: 0, right: 0, bottom: 0,
        background: 'var(--bs-surface, #141414)', border: '1px solid var(--bs-border, #1e1e1e)',
        borderRadius: '20px 20px 0 0', padding: '20px 20px 36px',
        zIndex: 51, animation: 'slideUp .25s ease',
        maxHeight: '85vh', overflowY: 'auto',
      }}>
        {/* Drag handle */}
        <div style={{ width: 36, height: 4, background: 'var(--bs-border, #2a2a2a)', borderRadius: 2, margin: '0 auto 20px' }} />

        {/* Source pill + LATEST / timestamp */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
          <span style={{
            fontSize: 10, fontFamily: 'JetBrains Mono', color: GREEN,
            border: `1px solid ${GREEN}44`, borderRadius: 10, padding: '2px 10px',
          }}>
            {source}
          </span>
          {isLatest ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              <div style={{ width: 6, height: 6, borderRadius: '50%', background: GREEN, animation: 'pulse 2s ease-in-out infinite' }} />
              <span style={{ fontSize: 9, fontFamily: 'JetBrains Mono', color: GREEN, fontWeight: 700, letterSpacing: 1 }}>LATEST</span>
            </div>
          ) : (
            <span style={{ fontSize: 9, fontFamily: 'JetBrains Mono', color: 'var(--bs-muted, #444)' }}>{published_at}</span>
          )}
        </div>

        {/* Headline */}
        <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--bs-ink, #e0e0e0)', lineHeight: 1.5, marginBottom: 8 }}>
          {headline}
        </div>

        {/* Score bar */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18 }}>
          <span style={{ fontSize: 11, fontFamily: 'JetBrains Mono', color: 'var(--bs-muted, #444)' }}>PayDirt Score</span>
          <span style={{ fontSize: 14, fontFamily: 'JetBrains Mono', fontWeight: 700, color: sc }}>
            {paydirt_score != null ? Math.round(paydirt_score) : '—'}
          </span>
          <div style={{ flex: 1, height: 3, background: 'var(--bs-border, #1a1a1a)', borderRadius: 2, overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${Math.min(100, paydirt_score || 0)}%`, background: sc, borderRadius: 2 }} />
          </div>
        </div>

        {/* Your Position card */}
        {bet && (
          <div style={{
            background: 'var(--bs-bg, #0e0e0e)', border: '1px solid var(--bs-border, #1a1a1a)',
            borderRadius: 12, padding: 14, marginBottom: 20,
          }}>
            <Label style={{ display: 'block', marginBottom: 8 }}>Your Position</Label>
            <div style={{ fontSize: 12, color: 'var(--bs-body, #777)', lineHeight: 1.5, marginBottom: 14 }}>
              {bet.market}
            </div>
            {/* 3-column grid */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
              {[
                { label: 'SIDE', value: side, valueColor: sdColor },
                { label: 'WAGERED', value: `$${fmt(bet.amount)}`, valueColor: AMBER },
                { label: 'TO WIN', value: `$${fmt(bet.potential_payout)}`, valueColor: GREEN },
              ].map(({ label, value, valueColor }) => (
                <div key={label} style={{
                  background: 'var(--bs-surface, #141414)', border: '1px solid var(--bs-border, #1e1e1e)',
                  borderRadius: 8, padding: '8px 10px', textAlign: 'center',
                }}>
                  <div style={{ fontSize: 8, fontFamily: 'JetBrains Mono', color: 'var(--bs-muted, #444)', marginBottom: 4 }}>{label}</div>
                  <div style={{ fontSize: 14, fontFamily: 'JetBrains Mono', fontWeight: 700, color: valueColor }}>{value}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Action buttons */}
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            onClick={() => url && window.open(url, '_blank')}
            style={{
              flex: 1, padding: 12, borderRadius: 12, background: 'var(--bs-border, #1a1a1a)',
              border: 'none', color: 'var(--bs-body, #777)', cursor: url ? 'pointer' : 'default',
              fontSize: 12, fontFamily: 'JetBrains Mono',
            }}
          >
            READ ARTICLE ↗
          </button>
          <button
            onClick={() => { setPage('markets'); onClose(); }}
            style={{
              flex: 1, padding: 12, borderRadius: 12, background: GREEN,
              border: 'none', color: '#000', cursor: 'pointer',
              fontSize: 12, fontFamily: 'JetBrains Mono', fontWeight: 700,
            }}
          >
            VIEW MARKET →
          </button>
        </div>
      </div>
    </>
  );
}

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────
export default function PositionsPage({ user, setPage, showAuth }) {
  const [summary, setSummary]             = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [feed, setFeed]                   = useState([]);
  const [feedLoading, setFeedLoading]     = useState(true);
  const [selected, setSelected]           = useState(null);
  const pollRef = useRef(null);

  // Redirect if not logged in
  useEffect(() => {
    if (!user) showAuth();
  }, [user, showAuth]);

  const fetchSummary = useCallback(async () => {
    try {
      const res = await apiFetch('/api/positions/summary');
      const data = await res.json();
      setSummary(data);
    } catch (e) { /* silent */ }
    finally { setSummaryLoading(false); }
  }, []);

  const fetchFeed = useCallback(async () => {
    try {
      const res = await apiFetch('/api/positions/feed');
      const data = await res.json();
      setFeed(data.articles || []);
    } catch (e) { /* silent */ }
    finally { setFeedLoading(false); }
  }, []);

  useEffect(() => {
    if (!user) return;
    fetchSummary();
    fetchFeed();
    pollRef.current = setInterval(fetchFeed, 60000);
    return () => clearInterval(pollRef.current);
  }, [user, fetchSummary, fetchFeed]);

  if (!user) {
    return (
      <div style={{ textAlign: 'center', padding: 60, color: 'var(--bs-muted, #555)' }}>
        <div style={{ fontSize: 13, fontFamily: 'JetBrains Mono', marginBottom: 16 }}>Sign in to view your positions</div>
        <button onClick={showAuth} style={{ background: GREEN, color: '#000', border: 'none', borderRadius: 8, padding: '10px 24px', fontWeight: 700, cursor: 'pointer', fontSize: 13 }}>
          Sign In
        </button>
      </div>
    );
  }

  const hasPositions = summary && summary.positions && summary.positions.length > 0;
  const noActiveBets = !summaryLoading && !hasPositions;

  return (
    <>
      {/* Inject slideUp keyframe */}
      <style>{`@keyframes slideUp{from{transform:translateY(100%)}to{transform:translateY(0)}}`}</style>

      <div style={{ maxWidth: 600, margin: '0 auto', padding: '0 12px 32px' }}>

        {/* ── Header ── */}
        <div style={{ padding: '20px 0 16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
            <div style={{ fontFamily: 'Dela Gothic One', fontSize: 24, color: 'var(--bs-ink, #e0e0e0)', letterSpacing: 1 }}>
              MY POSITIONS
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 5, background: 'var(--bs-border, #1a1a1a)', borderRadius: 20, padding: '3px 10px' }}>
              <div style={{ width: 6, height: 6, borderRadius: '50%', background: RED, animation: 'pulse 2s ease-in-out infinite' }} />
              <span style={{ fontSize: 9, fontFamily: 'JetBrains Mono', fontWeight: 700, color: RED, letterSpacing: 1 }}>LIVE</span>
            </div>
          </div>
          <div style={{ fontSize: 13, color: 'var(--bs-muted, #555)' }}>Latest news from sources you have money on</div>
        </div>

        {/* ── Open Positions Strip ── */}
        <Card style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <Label>Open Positions</Label>
            {!summaryLoading && summary && (
              <span style={{ fontSize: 11, fontFamily: 'JetBrains Mono', color: AMBER, fontWeight: 700 }}>
                ${fmt(summary.total_at_stake)} AT STAKE
              </span>
            )}
          </div>

          {summaryLoading ? (
            <div style={{ display: 'flex', gap: 8, overflowX: 'hidden' }}>
              <Skeleton w={120} h={34} style={{ borderRadius: 20 }} />
              <Skeleton w={140} h={34} style={{ borderRadius: 20 }} />
              <Skeleton w={110} h={34} style={{ borderRadius: 20 }} />
            </div>
          ) : !hasPositions ? (
            <div style={{ textAlign: 'center', padding: '12px 0' }}>
              <div style={{ fontSize: 12, color: 'var(--bs-muted, #555)', fontFamily: 'JetBrains Mono', marginBottom: 12 }}>
                No active positions yet — browse markets to place a bet
              </div>
              <button
                onClick={() => setPage('markets')}
                style={{ background: GREEN, color: '#000', border: 'none', borderRadius: 8, padding: '8px 20px', fontWeight: 700, cursor: 'pointer', fontSize: 12, fontFamily: 'JetBrains Mono' }}
              >
                BROWSE MARKETS →
              </button>
            </div>
          ) : (
            <div style={{ display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 4 }}>
              {summary.positions.map(pos => (
                <PositionChip key={pos.source} pos={pos} />
              ))}
            </div>
          )}
        </Card>

        {/* ── Article Feed ── */}
        {feedLoading ? (
          <>
            <SkeletonCard />
            <SkeletonCard />
            <SkeletonCard />
          </>
        ) : noActiveBets ? (
          /* ── Empty state ── */
          <div style={{ textAlign: 'center', padding: '48px 0' }}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>💼</div>
            <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--bs-ink, #e0e0e0)', marginBottom: 8 }}>No active positions</div>
            <div style={{ fontSize: 13, color: 'var(--bs-muted, #555)', marginBottom: 24 }}>
              Place a bet on a market to start tracking news here
            </div>
            <button
              onClick={() => setPage('markets')}
              style={{ background: GREEN, color: '#000', border: 'none', borderRadius: 8, padding: '10px 24px', fontWeight: 700, cursor: 'pointer', fontSize: 13, fontFamily: 'JetBrains Mono' }}
            >
              BROWSE MARKETS →
            </button>
          </div>
        ) : feed.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '32px 0', color: 'var(--bs-muted, #444)', fontSize: 12, fontFamily: 'JetBrains Mono' }}>
            No scored articles found for your positions yet
          </div>
        ) : (
          feed.map((article, i) => (
            <div key={article.id}>
              {article.isLatest && (
                <LatestLabel source={article.source} publishedAt={article.published_at} />
              )}
              <ArticleCard article={article} index={i} onTap={setSelected} />
            </div>
          ))
        )}
      </div>

      {/* ── Bottom sheet ── */}
      <BottomSheet article={selected} onClose={() => setSelected(null)} setPage={setPage} />
    </>
  );
}
